export interface LiveCatalogNamespace {
  name: string;
  namespace: string;
  location_ids?: string[];
}

export interface LiveCatalogLocation {
  id: string;
  name: string;
  namespace: string;
}

export interface LiveCatalogResponse {
  namespaces: LiveCatalogNamespace[];
  locations: LiveCatalogLocation[];
}

/** Keeps stored namespaces in order. The slug is the label until a search replaces it. */
export function storedNamespaceSelections(
  namespaces: string[],
): LiveCatalogSelection[] {
  return namespaces.flatMap((namespace) => {
    const trimmed = namespace.trim();
    if (!trimmed) {
      return [];
    }
    return [{ id: trimmed, name: trimmed, namespace: trimmed }];
  });
}

/** Keeps stored location ids in order, using the saved name when one exists. */
export function storedLocationSelections(
  locationIds: string[],
  locationNames: string[] | undefined,
): LiveCatalogSelection[] {
  return locationIds.flatMap((id, index) => {
    const trimmed = id.trim();
    if (!trimmed) {
      return [];
    }
    const name = locationNames?.[index]?.trim() || trimmed;
    return [{ id: trimmed, name, namespace: "" }];
  });
}

/** One chosen business or location. `id` is the namespace or the location id. */
export interface LiveCatalogSelection {
  id: string;
  name: string;
  namespace: string;
}

/**
 * Keeps stored ids that still exist in the catalog, in the stored order.
 * Free-text names that are not catalog ids are left out.
 */
export function matchCatalogSelections(
  catalog: LiveCatalogResponse,
  scope: "namespaces" | "locations",
  stored: { locationIds: string[]; namespaces: string[] },
): LiveCatalogSelection[] {
  if (scope === "namespaces") {
    const byNamespace = new Map(
      catalog.namespaces.map((item) => [item.namespace.toLowerCase(), item]),
    );
    return stored.namespaces.flatMap((namespace) => {
      const match = byNamespace.get(namespace.trim().toLowerCase());
      if (!match) {
        return [];
      }
      return [
        {
          id: match.namespace,
          name: match.name,
          namespace: match.namespace,
        },
      ];
    });
  }

  const byId = new Map(catalog.locations.map((item) => [item.id, item]));
  return stored.locationIds.flatMap((id) => {
    const match = byId.get(id.trim());
    if (!match) {
      return [];
    }
    return [
      {
        id: match.id,
        name: match.name,
        namespace: match.namespace,
      },
    ];
  });
}
